import { debounce } from '../utils/helper';

// Scroll Depth START
let distanceScrolled = -1;
let distanceThreshold = [
    { threshold: 0, value: 'Baseline', sent: false },
    { threshold: 25, value: '25%', sent: false },
    { threshold: 50, value: '50%', sent: false },
    { threshold: 75, value: '75%', sent: false },
    { threshold: 100, value: '100%', sent: false },
];

const scrollDepth = () => {
    const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
    let progress = Math.round(
        (scrollTop / (document.body.scrollHeight - window.innerHeight)) * 100
    );

    if (isNaN(progress)) progress = 0;

    if (progress > distanceScrolled) {
        distanceScrolled = progress;

        for (let i = 0; i < distanceThreshold.length; i++) {
            const status = distanceThreshold[i].sent;
            const threshold = distanceThreshold[i].threshold;
            const value = distanceThreshold[i].value;

            if (!status && progress >= threshold) {
                if ('dataLayer' in window) {
                    window.dataLayer.push({
                        event: 'scrollDepth',
                        scrollDistance: value,
                    });
                    distanceThreshold[i].sent = true;
                }
            }
        }
    }
};

const scrollListener = debounce(scrollDepth, 18);
// Scroll Depth END

// VIDEO START
const videoPercentages = [
    { percentage: '10%', value: 0.1, sent: false },
    { percentage: '20%', value: 0.2, sent: false },
    { percentage: '30%', value: 0.3, sent: false },
    { percentage: '40%', value: 0.4, sent: false },
    { percentage: '50%', value: 0.5, sent: false },
    { percentage: '60%', value: 0.6, sent: false },
    { percentage: '70%', value: 0.7, sent: false },
    { percentage: '80%', value: 0.8, sent: false },
    { percentage: '90%', value: 0.9, sent: false },
    { percentage: '100%', value: 1, sent: false },
];

let videoSeconds = [];

const videoTime = (video, title) => {
    // Send video event after time lapsed, every 10% interval
    const currentPercentage = video.percent;
    const unsentIndex = videoPercentages.findIndex((percentageValue) => {
        const currentPercentageValue = (Math.floor(currentPercentage * 10) / 10).toFixed(1);
        return !percentageValue.sent && Number(currentPercentageValue) === percentageValue.value;
    });
    if (unsentIndex >= 0) {
        window.dataLayer.push({
            event: 'videoPercentage',
            videoCategory: 'video-interval-percentage',
            videoFile: title,
            videoPercentage: videoPercentages[unsentIndex].percentage,
        });
        videoPercentages[unsentIndex].sent = true;
    }

    // Send video event after time lapsed, every 10s interval
    for (let i = 0; i < video.duration; i += 10) {
        if (!videoSeconds.find((threshold) => threshold.value === i)) {
            videoSeconds.push({ value: i, sent: false });
        }
    }
    const secondsIndex = videoSeconds.findIndex((threshold) => {
        const currentSeconds = Math.floor(video.seconds / 10) * 10;
        return !threshold.sent && Number(currentSeconds) === threshold.value;
    });
    if (secondsIndex >= 0) {
        window.dataLayer.push({
            event: 'videoSeconds',
            videoCategory: 'video-interval-seconds',
            videoFile: title,
            videoSeconds: videoSeconds[secondsIndex].value,
        });
        videoSeconds[secondsIndex].sent = true;
    }
};

const videoTimeListener = debounce(videoTime, 100);

const videoPlayListener = (v) => {
    if (typeof window !== 'undefined') {
        window.dataLayer.push({
            event: 'videoStart',
            videoFile: v,
            videoStatus: 'Start',
        });
    }
};

const videoEndListener = (v) => {
    if (typeof window !== 'undefined') {
        window.dataLayer.push({
            event: 'videoEnd',
            videoFile: v,
            videoStatus: 'End',
        });
    }
};
// VIDEO END

const GTM = {
    init: () => {
        scrollDepth();
        document.addEventListener('scroll', scrollListener);
    },
    initVideo: (player, title) => {
        const titleForGTM = title.replace('.', '');

        if ('dataLayer' in window) {
            player.on('play', () => {
                videoPlayListener(titleForGTM);
            });

            player.on('ended', () => {
                videoEndListener(titleForGTM);
            });

            player.on('timeupdate', (e) => {
                videoTimeListener(e, titleForGTM);
            });

            player.on('click', () => {
                if (videoSeconds[videoSeconds.length - 1].value < 10) {
                    videoPlayListener(titleForGTM);
                }
            });
        }
    },
    handleFormSubmit: (formName) => {
        if ('dataLayer' in window) {
            window.dataLayer.push({
                event: 'formSubmit',
                formName,
            });
        }
    },
    handleFormErrors: (formName, fieldName, fieldErrorName) => {
        if ('dataLayer' in window) {
            window.dataLayer.push({
                event: 'formFieldError',
                formName,
                fieldName,
                fieldErrorName,
            });
        }
    },
    handleGTMFieldError: (formName) => {
        setTimeout(() => {
            const allInputs = document.querySelectorAll(
                'input.error, select.error, .kf-select-toggle.error'
            );
            const allErrorMsg = document.querySelectorAll('.error-message');

            const specialCases = {
                dob: 'date_of_birth',
            };

            allInputs.forEach((input) => {
                const isSpecialCase = Object.entries(specialCases).find(
                    ([errorKey, inputId]) => input.id === inputId
                );

                if (isSpecialCase) {
                    const errorMsg = Array.from(allErrorMsg).find(
                        (err) => err.id === `error-${isSpecialCase[0]}`
                    );
                    if (errorMsg) {
                        GTM.handleFormErrors(formName, input.id, errorMsg.innerText);
                    }
                } else {
                    const normalizedInputId = input.id.replace(/_([a-z])/g, (match, letter) =>
                        letter.toUpperCase()
                    );

                    const errorMsg = Array.from(allErrorMsg).find(
                        (err) => err.id === `error-${normalizedInputId}`
                    );

                    if (errorMsg) {
                        GTM.handleFormErrors(formName, input.id, errorMsg.innerText);
                    }
                }
            });
        }, 500);
    },
    modal: (event_name, action, label) => {
        if ('dataLayer' in window) {
            window.dataLayer.push({
                event: event_name,
                modal_action: action,
                modal_label: label,
            });
        }
    },
    expandableContent: (action, label) => {
        if ('dataLayer' in window) {
            window.dataLayer.push({
                event: 'expandableContent',
                expandableContent_action: action,
                expandableContent_label: label,
            });
        }
    },
    inputChange: (formName, fieldName) => {
        if ('dataLayer' in window) {
            window.dataLayer.push({
                event: 'inputChange',
                formName,
                fieldName,
            });
        }
    },
    reset: () => {
        videoPercentages.forEach((threshold) => (threshold.sent = false));
        videoSeconds = [];
        distanceScrolled = -1;
        distanceThreshold.forEach((threshold) => (threshold.sent = false));
    },
};

export default GTM;
